import { enUS, type FormatDistanceFnOptions, type Locale } from 'date-fns/locale'
import { differenceInHours, differenceInMinutes, format, formatDuration, isValid } from 'date-fns'
import type { TranslationKey } from '@widgets/shared/services/translate/types'
import translateService from '@widgets/shared/services/translate/translateService'
import type { RecordAsConstValuesType } from '@/types'
import type { DateAlike } from '@/services/dateTimeService'

export const DATE_FORMAT = {
  TIME: 'h:mm aaa', // 10:51 am
  DATE: 'd MMM', // 2 May
}
type DateFormatType = RecordAsConstValuesType<typeof DATE_FORMAT>
export type DurationFormattingParams = {
  addSuffix?: boolean
  shortFormatDistance?: boolean
}

export default class DateTimeService {
  public static format(date: Date | number | string, formatString: DateFormatType | string): string {
    return format(date, formatString, { locale: DateTimeService.getLocale() })
  }

  public static getLocale(): Locale {
    return enUS
  }

  public toDate(rawDate?: DateAlike): Date {
    if (!rawDate) {
      return new Date()
    }

    const date = new Date(rawDate)
    if (isValid(date)) {
      return date
    }

    // Old Firefox versions are unable to parse ISO8601 format without Z char, so we're trying to fix this
    if (typeof rawDate === 'string') {
      const iso8601Date = new Date(rawDate.replace(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})(\+00)$/, '$1Z'))
      if (isValid(iso8601Date)) {
        return iso8601Date
      }
    }

    // Safari 14 can't parse this too
    if (typeof rawDate === 'string') {
      const iso8601Date = new Date(rawDate.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(\+00)$/, '$1T$2$3'))
      if (isValid(iso8601Date)) {
        return iso8601Date
      }
    }

    return date
  }

  public now(): Date {
    return this.toDate()
  }

  public isLessOneDay(date: Date): boolean {
    const oneDay = 24 * 60 * 60 * 1000
    return Math.abs(new Date().getTime() - new Date(date).getTime()) < oneDay
  }

  public isLessOneMinute(date: Date): boolean {
    const minute = 60 * 1000
    return Math.abs(new Date().getTime() - new Date(date).getTime()) < minute
  }

  public isLessOneHour(date: Date): boolean {
    const hour = 60 * 60 * 1000
    return Math.abs(new Date().getTime() - new Date(date).getTime()) < hour
  }

  public formatDurationInHoursAndMinutes(dateLeft: Date, dateRight?: Date, params?: DurationFormattingParams): string {
    const _dateRight = dateRight || this.now()
    return formatDuration(
      {
        hours: Math.abs(differenceInHours(dateLeft, _dateRight)),
        minutes: Math.abs(differenceInMinutes(dateLeft, _dateRight) % 60) || 1,
      },
      { locale: this.getLocaleForDistance(params) },
    )
  }

  public formatDurationInHours(dateLeft: Date, dateRight?: Date, params?: DurationFormattingParams): string {
    const _dateRight = dateRight || this.now()
    return formatDuration(
      {
        hours: Math.abs(differenceInHours(dateLeft, _dateRight)),
      },
      { locale: this.getLocaleForDistance(params) },
    )
  }

  protected formatDistanceLocale: Record<string, TranslationKey | Record<string, TranslationKey>> = {
    lessThanXSeconds: {
      one: 'formatDistance.lessThanXSeconds.one',
      other: 'formatDistance.lessThanXSeconds.other',
    },
    xSeconds: {
      one: 'formatDistance.xSeconds.one',
      other: 'formatDistance.xSeconds.other',
    },
    halfAMinute: 'formatDistance.halfAMinute',
    lessThanXMinutes: {
      one: 'formatDistance.lessThanXMinutes.one',
      other: 'formatDistance.lessThanXMinutes.other',
    },
    xMinutes: {
      one: 'formatDistance.xMinutes.one',
      oneShort: 'formatDistance.xMinutes.oneShort',
      other: 'formatDistance.xMinutes.other',
      otherShort: 'formatDistance.xMinutes.otherShort',
    },
    aboutXHours: {
      one: 'formatDistance.aboutXHours.one',
      other: 'formatDistance.aboutXHours.other',
    },
    xHours: {
      one: 'formatDistance.xHours.one',
      oneShort: 'formatDistance.xHours.oneShort',
      other: 'formatDistance.xHours.other',
      otherShort: 'formatDistance.xHours.otherShort',
    },
    xDays: {
      one: 'formatDistance.xDays.one',
      other: 'formatDistance.xDays.other',
    },
    aboutXWeeks: {
      one: 'formatDistance.aboutXWeeks.one',
      other: 'formatDistance.aboutXWeeks.other',
    },
    xWeeks: {
      one: 'formatDistance.xWeeks.one',
      other: 'formatDistance.xWeeks.other',
    },
    aboutXMonths: {
      one: 'formatDistance.aboutXMonths.one',
      other: 'formatDistance.aboutXMonths.other',
    },
    xMonths: {
      one: 'formatDistance.xMonths.one',
      other: 'formatDistance.xMonths.other',
    },
    aboutXYears: {
      one: 'formatDistance.aboutXYears.one',
      other: 'formatDistance.aboutXYears.other',
    },
    xYears: {
      one: 'formatDistance.xYears.one',
      other: 'formatDistance.xYears.other',
    },
    overXYears: {
      one: 'formatDistance.overXYears.one',
      other: 'formatDistance.overXYears.other',
    },
    almostXYears: {
      one: 'formatDistance.almostXYears.one',
      other: 'formatDistance.almostXYears.other',
    },
  }

  protected getLocaleForDistance({
    addSuffix = false,
    shortFormatDistance = false,
  }: DurationFormattingParams = {}): Locale {
    return {
      ...DateTimeService.getLocale(),
      formatDistance: (token: string, count: number, options?: FormatDistanceFnOptions) =>
        this.formatDistance(token, count, options ? { ...options, addSuffix } : { addSuffix }, shortFormatDistance),
    }
  }

  protected formatDistance(
    token: string,
    count: number,
    options: Record<string, unknown>,
    shortFormatDistance = false,
  ): string {
    const tokenValue = this.formatDistanceLocale[token]
    if (!tokenValue) {
      return count.toString()
    }
    let result: string
    if (typeof tokenValue === 'string') {
      result = tokenValue
    } else if (count === 1) {
      let key = tokenValue.one
      if (shortFormatDistance && tokenValue.oneShort) {
        key = tokenValue.oneShort
      }
      result = translateService.t(key)
    } else {
      let key = tokenValue.other
      if (shortFormatDistance && tokenValue.otherShort) {
        key = tokenValue.otherShort
      }
      result = translateService.tc(key, count)
    }
    if (options.addSuffix) {
      if (options.comparison && Number(options.comparison) > 0) {
        return `in ${result}`
      }
      return `${result} ${translateService.t('common.ago')}`
    }
    return result
  }
}

export const dateTimeService = new DateTimeService()
